import type { ConfigurationSetup } from './config/config-setup.types';
import {
  ApiBackendError,
  ApiError,
  ApiHTTPError,
  ApiNetworkError,
  ApiSystemError,
} from './errors/ApiError';
import {
  AuthExpiredError,
  AuthLoginFailedError,
  AuthLogoutFailedError,
  AuthUnauthenticatedError,
} from './errors/AuthError';
import { TeoError } from './errors/TeoError';
import type { TeoCore } from './TeoCore';
import type { AnalysisConfiguration } from './text/TeoText';

/** @deprecated use ApiError instead. Legacy alias for slow refactoring */
export type APIError = ApiError;

/** @deprecated use ApiError.matches instead. Legacy alias for slow refactoring */
export const isAPIError = ApiError.matches;

export interface TeoApiConfig {
  apiBaseUrl: string;
  useInsecureHttp?: boolean;
}

export class TeoApi {
  apiToken: Maybe<string> = null;
  apiBaseUrl: string;
  apiFullUrl: string;
  useInsecureHttp: boolean;
  private core: TeoCore;
  constructor(core: TeoCore, { apiBaseUrl, useInsecureHttp = false }: TeoApiConfig) {
    this.core = core;
    this.apiBaseUrl = apiBaseUrl;
    this.useInsecureHttp = useInsecureHttp;
    this.apiFullUrl = `${apiBaseUrl}api/v3/`;
    console.log('API URL:', this.apiFullUrl);
  }

  async setApiToken(apiToken: string | null): Promise<true> {
    if (apiToken === null) {
      this.apiToken = '';
      this.core.user.clearUserDetail();
      return true;
    }
    // TODO: Add request to check if token is still valid
    this.apiToken = apiToken;
    return true;
  }

  async logIn(email: string, password: string) {
    const result = await handleResponse<UserDetail>(() =>
      fetch(`${this.apiFullUrl}login`, {
        method: 'POST',
        body: JSON.stringify({ email, password, insecure: this.useInsecureHttp }),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    );

    if (!result.ok) {
      return new AuthLoginFailedError(result.error.message, result.error);
    }

    if (!this.setApiToken(result.json.token)) {
      // TODO: how can this happen?
      return new AuthExpiredError();
    }
    return result.json;
  }

  async logOut() {
    // logOut is called from authenticatedGet, so logOut may not call it (because it will be an endless loop)
    try {
      const result = await handleResponse(() =>
        fetch(this.apiFullUrl + 'logout', {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${this.apiToken}`,
          },
        }),
      );
      if (result.ok) {
        return true;
      }
      return new AuthLogoutFailedError(result.error.message, result.error);
    } catch (error) {
      return new AuthLogoutFailedError(error instanceof Error ? error.message : String(error));
    } finally {
      this.setApiToken(null);
    }
  }

  async requestPasswordReset(email: string) {
    const result = await handleResponse<PWResetResult>(() =>
      fetch(`${this.apiFullUrl}resetPassword`, {
        method: 'POST',
        body: JSON.stringify({ email }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }),
    );
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Password reset request failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getSubscriptionConfig(subscriptionId: string | number) {
    try {
      // fake id for local testing
      const id = subscriptionId === 'localhost' ? 'v3-master.teo.digital' : subscriptionId;

      const endpoint =
        typeof subscriptionId === 'string' ? 'subscriptionConfigByDomain' : 'subscriptionConfig';
      const result = await handleResponse<SubscriptionConfig>(() =>
        fetch(`${this.apiFullUrl}${endpoint}/${id}`, {
          headers: {
            Accept: 'application/json',
          },
        }),
      );
      return result.ok ?
          result.json
        : new ApiBackendError(
            { error: 'Failed to get subscription config', message: result.error.message },
            result.status,
            result.error,
          );
    } catch (error) {
      return new ApiSystemError(error);
    }
  }

  async getConfigOptions(evaluationProfileId: number) {
    const result = await this.authenticatedGet<ConfigurationSetup>(
      `textConfigurationSetup/${evaluationProfileId}`,
    );

    return result.ok ?
        result.json
      : new ApiBackendError(
          {
            error: 'Failed to get config options',
            message: result.error.message,
          },
          result.status,
          result.error,
        );
  }

  async getAnalysis(request: AnalysisRequest) {
    request.client_name = 'v3-App';
    const result = await this.authenticatedPost<AnalysisResultData>('analyzeText', request);

    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Analysis failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getWordListScan(request: WordListScanRequest) {
    const result = await this.authenticatedPost('wordLists/scan', request);

    return result.ok ?
        (result.json as WordListScanResult)
      : new ApiBackendError(
          { error: 'Word list scan failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getAiPrompts(toolName: string) {
    const result = await this.authenticatedGet<AIPrompt[]>(`ai/prompts/${toolName}`);

    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Failed to get AI prompts', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getSimpleAiSuggestion(request: AiRequest) {
    const result = await this.authenticatedPost<SimpleAiSuggestionData>('ai/suggestions', request);

    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'AI suggestion failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getAiSuggestion(request: AiRequest) {
    const result = await this.authenticatedPost<AiSuggestionData>('ai/suggestions', request);
    console.log('AI suggestion result:', result);
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'AI suggestion failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getLanguageToolCheck(request: LanguageToolRequest) {
    const result = await this.authenticatedPost<LanguageToolCheckData>('lt/check', request);
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'LanguageTool check failed', message: result.error.message },
          result.status,
          result.error,
        );
  }

  async getHelpTopic(keyWord: string) {
    const result = await this.authenticatedGet<ApiHelpTopic>(`help/${keyWord}`);
    return result.ok ?
        result.json
      : new ApiBackendError(
          { error: 'Failed to get help topic', message: result.error.message },
          result.status,
          result.error,
        );
  }

  private async authenticatedGet<TData>(requestName: string) {
    return this.handleAuthenticatedResponse<TData>(() =>
      fetch(this.apiFullUrl + requestName, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${this.apiToken}`,
        },
      }),
    );
  }

  public async authenticatedPost<TData>(requestName: string, data: unknown) {
    return this.handleAuthenticatedResponse<TData>(async () =>
      fetch(this.apiFullUrl + requestName, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiToken}`,
        },
      }),
    );
  }

  private async handleAuthenticatedResponse<TData>(call: () => Promise<Response>) {
    if (!this.apiToken) {
      this.core.user.logOut();
      return errorResult(new AuthUnauthenticatedError(), 403);
    }

    try {
      const response = await call();
      const result = await responseJsonOrError<TData>(response);
      if (!result.ok) {
        if (result.error instanceof AuthUnauthenticatedError) {
          this.core.user.logOut(false);
          return errorResult(result.error, 403);
        }
      }
      return result;
    } catch (error) {
      if (error instanceof TypeError) {
        return errorResult(new ApiNetworkError(error));
      }
      return errorResult(new ApiSystemError(error));
    }
  }
}

// --- error/result handling ---
type OkResult<TData> = { ok: true; json: TData };
type ErrorResult<TError extends TeoError> = { ok: false; error: TError; status: number };

const okResult = <TData>(json: TData): OkResult<TData> => ({ ok: true, json });

function errorResult<TError extends ApiError>(error: TError): ErrorResult<TError>;
function errorResult<TError extends TeoError>(error: TError, status: number): ErrorResult<TError>;
function errorResult<TError extends TeoError>(error: TError, status?: number): ErrorResult<TError> {
  return {
    ok: false,
    error,
    status: ApiError.matches(error) ? (error as unknown as ApiError).status : (status ?? 0),
  };
}

async function handleResponse<TData>(call: () => Promise<Response>) {
  try {
    const response = await call();
    return responseJsonOrError<TData>(response);
  } catch (error) {
    if (error instanceof TypeError) {
      return errorResult(new ApiNetworkError(error));
    }
    return errorResult(new ApiSystemError(error));
  }
}

async function responseJsonOrError<TData>(response: Response) {
  try {
    const json = await response.json();
    if (response.ok) return okResult(json as TData);
    if (isApiErrorResponse(json) && response.status != 401 && response.status != 403) {
      return errorResult(new ApiBackendError(json, response.status));
    } else {
      const httpError = new ApiHTTPError(response);
      if (httpError.response.status === 403 || httpError.response.status === 401) {
        return errorResult(new AuthUnauthenticatedError(httpError), httpError.response.status);
      }
      return errorResult(httpError);
    }
  } catch (error) {
    return errorResult(new ApiSystemError(error));
  }
}

function isApiErrorResponse(response: unknown): response is { message: string } {
  return typeof response === 'object' && response !== null && 'message' in response;
}

// --- types ---

export interface UserDetail {
  token: string;
  email: string;
  name: string;
  role: UserRole;
  modules: string[];
}

export interface UserRole {
  level: number;
  name: string;
  permissions: string[];
}

export interface AnalysisResultData {
  report_id: string;
  created_at: number;
  tpi: number;
  markers: APITeoMarker[];
  indices: Record<string, SubEvaluation>;
  text_session: string;
  instructions: AssistantInstruction[];
  additional_instructions: Instruction[];
  additional_values: AnalysisStatistics & {
    num_whitelisted_words: number;
    num_passive_sentences: number;
    num_dialog_particles: number;
    num_dialog_phrases: number;
    num_causal_connectors: number;
    percent_you: number;
  };
}

export type AnalysisStatisticsEntryId =
  | 'num_words'
  | 'num_long_words'
  | 'num_sentences'
  | 'num_long_sentences'
  | 'num_substantives'
  | 'num_abstracts'
  | 'num_personal_words'
  | 'num_personal_sentences'
  | 'num_impuls_sentences'
  | 'percent_long_words'
  | 'percent_long_sentences'
  | 'percent_substantives'
  | 'percent_abstracts'
  | 'percent_we'
  | 'percent_you'
  | 'word_length'
  | 'sentence_length'
  | 'impuls_weight'
  | 'impuls_weight_per_sentence';

export type AnalysisStatistics = Partial<Record<AnalysisStatisticsEntryId, Maybe<number>>>;

export interface Instruction {
  id: number;
  short_title: string;
  title: string;
  description: string;
  text_property_name: string;
  ai_prompts: AIPrompt[];
}
export interface AssistantInstruction extends Instruction {
  expected_tpi_change: number;
  expected_tpi_change_minute: number;
  priority_score: number;
  effort_estimate: number;
  doability_factor: number;
  index_name: string;
}

export interface AIPrompt {
  id: number;
  title: string;
  description: string;
  type: string;
  available?: boolean;
}

export interface TextProperty {
  title: string;
  num: number;
  marker_names: string[];
}

export interface SubEvaluation {
  title: string;
  score: number;
  text_properties: Record<string, TextProperty>;
  used: boolean;
}

export interface AnalysisRequest {
  text: string | TextBlock[];
  audience: number;
  strategy: number;
  medium: number;
  is_informal: number;
  use_whitelist?: boolean;
  client_name?: string;
  last_text_property?: string;
  last_instruction_id?: number;
  text_alias?: string;
  use_exp_parser?: boolean;
}

export interface TextBlock {
  string: string;
  type: 'text' | 'paragraph' | 'linebreak' | 'whitespace';
  length?: number;
  interpret_as?: string;
}

export interface APITeoMarker {
  marker: string;
  offset: number;
  length: number;
  relation: number | null;
  groupName?: MarkerGroupName;
  color?: MarkerColor;
}

export type MarkerColor = 'error' | 'warning' | 'neutral' | 'correct';

export type MarkerGroupName = 'analysis' | 'wordlist' | 'spellcheck' | 'diff';

export interface AiRequest {
  context: ExcerptContext;
  tool: string;
  tool_id: number;
  config: AnalysisConfiguration & { evaluation_profile: number };
}

export interface ExcerptContext {
  text: string;
  textBefore: string;
  textAfter: string;
  textFocus: string;
  replacementWrapper: number;
}

// incomplete
export interface AiSuggestionData {
  suggestions: AiSuggestion[];
  bad_results: AiSuggestion[];
  base_result: {
    tpi: {
      percentage: number;
    };
  };
  error: boolean;
  error_message: string;
}

export interface AiSuggestion {
  text: string;
  teo_result: {
    tpi: {
      percentage: number;
    };
  };
}

// incomplete
export interface SimpleAiSuggestionData {
  suggestions: string[];
  error: boolean;
  error_message: string;
}

export type WordListScanRequest = {
  text: TextBlock[];
  ignored_entries: {
    word_list_id: number;
    entry_id: number;
  }[];
};

export type WordListScanResult = {
  word_lists: WordListData[];
  markers: APITeoMarker[];
};

export type WordListData = {
  id: number;
  current_text_entries: WordListEntry[];
  description: string;
  name: string;
  order: number;
  relation_type: number;
  title: string;
  use_for_ct: boolean;
};

export type WordListEntry = {
  id: number;
  current_relation: number;
  description: string;
  expressions: WordListExpression[];
  found_by: number;
  found_string: string;
  main_expression_id: number;
  title: string;
  word_list_id: number;
};

export type WordListExpression = {
  id: number;
  expression_type: number;
  full_expression: string;
  is_regex: boolean;
  priority: number;
  word_list_id: number;
  word_list_entry_id: number;
};

export type LanguageToolRequest = {
  data: {
    annotation: LanguageToolTextBlock[];
  };
  language: string;
};

export type LanguageToolTextBlock = {
  text?: string;
  markup?: string;
  interpretAs?: string;
};

export interface LanguageToolCheckData {
  software: {
    name: string;
    version: string;
    buildDate: string;
    apiVersion: number;
    status?: string;
    premium?: boolean;
  };
  language: {
    name: string;
    code: string;
    detectedLanguage: {
      name: string;
      code: string;
    };
  };
  matches: Array<{
    message: string;
    shortMessage?: string;
    offset: number;
    length: number;
    replacements: Array<{
      value: string;
    }>;
    context: {
      text: string;
      offset: number;
      length: number;
    };
    sentence: string;
    rule: {
      id: string;
      subId?: string;
      description: string;
      urls?: Array<{ value: string }>;
      issueType?: string;
      category?: {
        id?: string;
        name?: string;
      };
    };
  }>;
}

export type PWResetResult = {
  success: boolean;
};

export interface SubscriptionConfig {
  generic_users: {
    email: string;
    name: string;
    is_logged_in_api: boolean | 0 | 1;
  }[];
  use_generic_users: boolean | 0 | 1;
  use_named_users: boolean | 0 | 1;
  customer_logo?: string;
  show_brandfit?: boolean | 0 | 1;
}

export type ApiHelpTopicSection = {
  key_word: string;
  title: string;
  text: string;
  sub_topics: {
    key_word: string;
    title: string;
    text: string;
  }[];
};

export type ApiHelpTopic = {
  key_word: string;
  title: string;
  sections: ApiHelpTopicSection[];
};
